// src/constants/paymentFailedOrders.js
// Payment Failed Orders module - shared vocabulary between the list, detail and summary views.
// Mirrors the PF_ACTION_* / trigger_reason / outcome_type values returned by the API.

/* ------------------------------------------------------------------ statuses */

export const PF_ACTION_PENDING_REVIEW = 'pending_review';
export const PF_ACTION_CONTACTED_AWAITING_CONFIRMATION =
  'contacted_awaiting_confirmation';
export const PF_ACTION_CONTACTED_REQUESTED_CALLBACK =
  'contacted_requested_callback';
export const PF_ACTION_CUSTOMER_NOT_REACHABLE = 'customer_not_reachable';
export const PF_ACTION_PLACED_NEW_ORDER = 'placed_new_order';
export const PF_ACTION_NOT_INTERESTED = 'not_interested';
export const PF_ACTION_DUPLICATE_ORDER = 'duplicate_order';
export const PF_ACTION_PRICE_CHANGE = 'price_change';
export const PF_ACTION_REPROCESSED_SUCCESSFULLY = 'reprocessed_successfully';
export const PF_ACTION_AUTO_RESOLVED_PAYMENT_SUCCEEDED =
  'auto_resolved_payment_succeeded';

// system: set by the crons, never offered in the agent dropdown
// closes: the API stamps closed_at when the record lands on this status
export const PAYMENT_FAILED_ACTION_STATUSES = [
  {
    value: PF_ACTION_PENDING_REVIEW,
    label: 'Pending Review',
    chipClass: 'pf-chip-pending',
    closes: false,
    system: true,
  },
  {
    value: PF_ACTION_CONTACTED_AWAITING_CONFIRMATION,
    label: 'Contacted – Awaiting Customer Confirmation',
    shortLabel: 'Awaiting Confirmation',
    chipClass: 'pf-chip-open',
    closes: false,
    system: false,
  },
  {
    value: PF_ACTION_CONTACTED_REQUESTED_CALLBACK,
    label: 'Contacted – Requested Call Back',
    shortLabel: 'Requested Call Back',
    chipClass: 'pf-chip-open',
    closes: false,
    system: false,
  },
  {
    value: PF_ACTION_CUSTOMER_NOT_REACHABLE,
    label: 'Customer Not Reachable',
    chipClass: 'pf-chip-closed-neutral',
    closes: true,
    system: false,
  },
  {
    value: PF_ACTION_PLACED_NEW_ORDER,
    label: 'Placed a New Order',
    chipClass: 'pf-chip-converted',
    closes: true,
    system: false,
    requiresResolvedOrderNo: true,
  },
  {
    value: PF_ACTION_NOT_INTERESTED,
    label: 'Not Interested',
    chipClass: 'pf-chip-closed-neutral',
    closes: true,
    system: false,
  },
  {
    value: PF_ACTION_DUPLICATE_ORDER,
    label: 'Duplicate Order',
    chipClass: 'pf-chip-closed-neutral',
    closes: true,
    system: false,
  },
  {
    value: PF_ACTION_PRICE_CHANGE,
    label: 'Price Change',
    chipClass: 'pf-chip-closed-neutral',
    closes: true,
    system: false,
  },
  {
    value: PF_ACTION_REPROCESSED_SUCCESSFULLY,
    label: 'Reprocessed Successfully',
    chipClass: 'pf-chip-converted',
    closes: true,
    system: false,
  },
  {
    value: PF_ACTION_AUTO_RESOLVED_PAYMENT_SUCCEEDED,
    label: 'Auto-Resolved (Customer Paid)',
    shortLabel: 'Auto-Resolved',
    chipClass: 'pf-chip-auto',
    closes: true,
    system: true,
  },
];

// Statuses an agent may set: everything the crons own is excluded
export const PAYMENT_FAILED_AGENT_STATUSES =
  PAYMENT_FAILED_ACTION_STATUSES.filter((status) => !status.system);

export const getPaymentFailedStatus = (value) =>
  PAYMENT_FAILED_ACTION_STATUSES.find((status) => status.value === value);

export const getPaymentFailedStatusLabel = (value) =>
  getPaymentFailedStatus(value)?.label || value || '-';

export const getPaymentFailedStatusShortLabel = (value) => {
  const status = getPaymentFailedStatus(value);
  return status?.shortLabel || status?.label || value || '-';
};

export const getPaymentFailedStatusChipClass = (value) =>
  getPaymentFailedStatus(value)?.chipClass || 'pf-chip-pending';

export const isPaymentFailedClosingStatus = (value) =>
  Boolean(getPaymentFailedStatus(value)?.closes);

export const requiresResolvedOrderNo = (value) =>
  Boolean(getPaymentFailedStatus(value)?.requiresResolvedOrderNo);

/* ------------------------------------------------------------ trigger reasons */

export const PF_TRIGGER_STANDARD = 'standard';
export const PF_TRIGGER_HIGH_VALUE = 'high_value';
export const PF_TRIGGER_REPEAT_FAILURE = 'repeat_failure';

export const PAYMENT_FAILED_TRIGGER_REASONS = [
  {
    value: PF_TRIGGER_STANDARD,
    label: 'Standard',
    shortLabel: 'Standard',
    urgent: false,
    hint: 'Picked up by the 30 minute batch',
  },
  {
    value: PF_TRIGGER_HIGH_VALUE,
    label: 'High Value',
    shortLabel: 'High Value',
    urgent: true,
    hint: 'Order above AED 4,999 - fast tracked every 10 minutes',
  },
  {
    value: PF_TRIGGER_REPEAT_FAILURE,
    label: 'Repeat Failure',
    shortLabel: 'Repeat x3',
    urgent: true,
    hint: '3 or more failures within 10 minutes - fast tracked',
  },
];

export const getPaymentFailedTrigger = (value) =>
  PAYMENT_FAILED_TRIGGER_REASONS.find((trigger) => trigger.value === value);

export const isUrgentTrigger = (value) =>
  Boolean(getPaymentFailedTrigger(value)?.urgent);

/* --------------------------------------------------------------- outcome type */

export const PF_OUTCOME_CONVERTED = 'converted';
export const PF_OUTCOME_NOT_CONVERTED = 'not_converted';

export const PAYMENT_FAILED_OUTCOME_TYPES = [
  { value: PF_OUTCOME_CONVERTED, label: 'Converted', chipClass: 'pf-chip-converted' },
  {
    value: PF_OUTCOME_NOT_CONVERTED,
    label: 'Not Converted',
    chipClass: 'pf-chip-closed-neutral',
  },
];

export const getPaymentFailedOutcomeLabel = (value) =>
  PAYMENT_FAILED_OUTCOME_TYPES.find((outcome) => outcome.value === value)?.label ||
  'Open';

/* ------------------------------------------------------------- other filters */

// record_state sent to the API
export const PAYMENT_FAILED_OPEN_CLOSED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open only' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed only' },
];

export const PAYMENT_FAILED_PAYMENT_MODES = [
  { label: 'All', value: '' },
  { label: 'PostPay', value: 'postpay' },
  { label: 'PayFort', value: 'payfort_fort_cc' },
  { label: 'Payfort Installments', value: 'payfort_fort_installments' },
  { label: 'Tamara', value: 'tamara' },
  { label: 'Payfort Apple Pay', value: 'apple_pay' },
  { label: 'Tap', value: 'tap' },
  { label: 'Tap Careem Pay', value: 'tap_careem_pay' },
  { label: 'Tap Apple Pay', value: 'tap_apple_pay' },
  { label: 'Checkout', value: 'checkoutcom_card_payment' },
  { label: 'Spotii', value: 'spotiipay' },
  { label: 'Tap Knet', value: 'tap_knet' },
  { label: 'Payfort Omannet', value: 'payfort_omannet' },
  { label: 'Cashew', value: 'cashew' },
];
